<!--
  Copyright (c) 2025 NoorWays. All rights reserved.
  Licensed under the terms specified in the LICENSE.txt file.
-->

<template>
  <div class="support">
    <div class="container">
      <div class="support-header">
        <h1>{{ $t('support.title') }}</h1>
        <p class="support-description">{{ $t('support.description') }}</p>
      </div>

      <div class="support-content">
        <!-- Quick Help Section -->
        <section class="support-section">
          <h2>{{ $t('support.quickHelp.title') }}</h2>
          <div class="help-grid">
            <div class="help-card">
              <div class="help-icon">📧</div>
              <h3>{{ $t('support.contact.email.title') }}</h3>
              <p>{{ $t('support.contact.email.description') }}</p>
              <button
                @click="openSupportEmail"
                :class="['btn', 'btn-primary', { 'btn-loading': emailStatus === 'loading' }]"
                :disabled="emailStatus === 'loading'"
              >
                <span v-if="emailStatus === 'loading'">Opening email...</span>
                <span v-else-if="emailStatus === 'success'">✅ Email opened!</span>
                <span v-else>{{ $t('support.contact.email.button') }}</span>
              </button>

              <!-- Error message -->
              <div v-if="emailStatus === 'error' && emailError" class="email-error">
                <p class="error-message">{{ emailError }}</p>
              </div>

              <!-- Success message with additional info -->
              <div v-if="emailStatus === 'success'" class="email-success">
                <p class="success-message">
                  {{ $t('support.contact.email.fallbackMessage') }}
                  <a href="#" @click.prevent="openGeneralSupportEmail" class="email-link"
                    >contact@noorways.fr</a
                  >
                </p>
              </div>
            </div>

            <div class="help-card">
              <div class="help-icon">❓</div>
              <h3>{{ $t('support.faq.title') }}</h3>
              <p>{{ $t('support.faq.description') }}</p>
              <button @click="scrollToFaq" class="btn btn-secondary">
                {{ $t('support.faq.button') }}
              </button>
            </div>

            <div class="help-card">
              <div class="help-icon">🚀</div>
              <h3>{{ $t('support.gettingStarted.title') }}</h3>
              <p>{{ $t('support.gettingStarted.description') }}</p>
              <button @click="scrollToGettingStarted" class="btn btn-secondary">
                {{ $t('support.gettingStarted.button') }}
              </button>
            </div>
          </div>
        </section>

        <!-- Getting Started Section -->
        <section id="getting-started" class="support-section">
          <h2>{{ $t('support.gettingStarted.title') }}</h2>
          <div class="steps-container">
            <div class="step">
              <div class="step-number">1</div>
              <div class="step-content">
                <h3>{{ $t('support.gettingStarted.step1.title') }}</h3>
                <p>{{ $t('support.gettingStarted.step1.description') }}</p>
              </div>
            </div>
            <div class="step">
              <div class="step-number">2</div>
              <div class="step-content">
                <h3>{{ $t('support.gettingStarted.step2.title') }}</h3>
                <p>{{ $t('support.gettingStarted.step2.description') }}</p>
              </div>
            </div>
            <div class="step">
              <div class="step-number">3</div>
              <div class="step-content">
                <h3>{{ $t('support.gettingStarted.step3.title') }}</h3>
                <p>{{ $t('support.gettingStarted.step3.description') }}</p>
              </div>
            </div>
          </div>
        </section>

        <!-- FAQ Section -->
        <section id="faq" class="support-section">
          <h2>{{ $t('support.faq.title') }}</h2>
          <div class="faq-container">
            <div
              v-for="(faq, index) in faqs"
              :key="index"
              class="faq-item"
              :class="{ active: activeFaq === index }"
            >
              <button
                @click="toggleFaq(index)"
                class="faq-question"
                :aria-expanded="activeFaq === index"
              >
                <span>{{ $t(faq.question) }}</span>
                <span class="faq-icon">{{ activeFaq === index ? '−' : '+' }}</span>
              </button>
              <div class="faq-answer" v-show="activeFaq === index">
                <div
                  v-if="faq.answer === 'support.faq.items.installation.answer'"
                  class="faq-answer-content"
                >
                  <p>
                    {{ $t(faq.answer) }}
                    <a
                      :href="$t('support.faq.items.installation.linkUrl')"
                      target="_blank"
                      rel="noopener noreferrer"
                      class="faq-link"
                    >
                      {{ $t('support.faq.items.installation.linkText') }} </a
                    >.
                  </p>
                </div>
                <div
                  v-else-if="faq.answer === 'support.faq.items.troubleshooting.answer'"
                  class="faq-answer-content"
                >
                  <p>
                    {{ $t(faq.answer) }}
                    <a href="#" @click.prevent="openGeneralSupportEmail" class="faq-link">
                      contact@noorways.fr</a
                    >.
                  </p>
                </div>
                <div v-else class="faq-answer-content">
                  <p>{{ $t(faq.answer) }}</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <!-- Contact Information Section -->
        <section class="support-section">
          <h2>{{ $t('support.contact.title') }}</h2>
          <div class="contact-info">
            <div class="contact-item">
              <strong>{{ $t('support.contact.email.label') }}:</strong>
              <a href="#" @click.prevent="openGeneralSupportEmail" class="email-link"
                >contact@noorways.fr</a
              >
            </div>
            <div class="contact-item">
              <strong>{{ $t('support.contact.languages.label') }}:</strong>
              {{ $t('support.contact.languages.value') }}
            </div>
          </div>
        </section>

        <!-- Extension Information Section -->
        <section class="support-section">
          <h2>{{ $t('support.extension.title') }}</h2>
          <div class="extension-info">
            <p>{{ $t('support.extension.description') }}</p>
            <div class="extension-links">
              <a
                href="https://chromewebstore.google.com/detail/fjlfoaokggeemlcoemkhokccampibipm"
                target="_blank"
                class="btn btn-outline"
              >
                {{ $t('support.extension.chromeStore') }}
              </a>
            </div>
          </div>
        </section>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { useHead } from '@vueuse/head'
import { useI18n } from 'vue-i18n'
import { openEmailWithI18nTemplate } from '@/utils/emailUtils'
import { logger } from '@/services/logger'

const { t, locale } = useI18n()

// State for user feedback
const emailStatus = ref<'idle' | 'loading' | 'success' | 'error'>('idle')
const emailError = ref<string | null>(null)

useHead({
  title: () => `${t('support.title')} - n.writer`,
  meta: [
    {
      name: 'description',
      content: () => t('support.meta.description'),
    },
    {
      name: 'keywords',
      content: () => t('support.seo.keywords'),
    },
  ],
  script: [
    // BreadcrumbList Schema
    {
      type: 'application/ld+json',
      children: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: 'n.writer',
            item: 'https://n-writer.app',
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: t('support.title'),
            item: 'https://n-writer.app/support',
          },
        ],
      }),
    },
    // ContactPage Schema
    {
      type: 'application/ld+json',
      children: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'ContactPage',
        name: t('support.title'),
        description: t('support.meta.description'),
        url: 'https://n-writer.app/support',
        mainEntity: {
          '@type': 'Organization',
          name: 'n.writer',
          email: 'contact@noorways.fr',
          contactPoint: {
            '@type': 'ContactPoint',
            email: 'contact@noorways.fr',
            contactType: 'customer support',
            availableLanguage: ['English', 'French', 'Spanish'],
          },
        },
      }),
    },
    // FAQPage Schema - leveraging existing FAQ content
    {
      type: 'application/ld+json',
      children: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: [
          {
            '@type': 'Question',
            name: t('support.faq.items.installation.question'),
            acceptedAnswer: {
              '@type': 'Answer',
              text: t('support.faq.items.installation.answer'),
            },
          },
          {
            '@type': 'Question',
            name: t('support.faq.items.usage.question'),
            acceptedAnswer: {
              '@type': 'Answer',
              text: t('support.faq.items.usage.answer'),
            },
          },
          {
            '@type': 'Question',
            name: t('support.faq.items.premium.question'),
            acceptedAnswer: {
              '@type': 'Answer',
              text: t('support.faq.items.premium.answer'),
            },
          },
          {
            '@type': 'Question',
            name: t('support.faq.items.languages.question'),
            acceptedAnswer: {
              '@type': 'Answer',
              text: t('support.faq.items.languages.answer'),
            },
          },
          {
            '@type': 'Question',
            name: t('support.faq.items.privacy.question'),
            acceptedAnswer: {
              '@type': 'Answer',
              text: t('support.faq.items.privacy.answer'),
            },
          },
          {
            '@type': 'Question',
            name: t('support.faq.items.troubleshooting.question'),
            acceptedAnswer: {
              '@type': 'Answer',
              text: t('support.faq.items.troubleshooting.answer'),
            },
          },
        ],
      }),
    },
    // HowTo Schema for Getting Started
    {
      type: 'application/ld+json',
      children: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'HowTo',
        name: t('support.gettingStarted.title'),
        description: t('support.gettingStarted.description'),
        step: [
          {
            '@type': 'HowToStep',
            position: 1,
            name: t('support.gettingStarted.step1.title'),
            text: t('support.gettingStarted.step1.description'),
          },
          {
            '@type': 'HowToStep',
            position: 2,
            name: t('support.gettingStarted.step2.title'),
            text: t('support.gettingStarted.step2.description'),
          },
          {
            '@type': 'HowToStep',
            position: 3,
            name: t('support.gettingStarted.step3.title'),
            text: t('support.gettingStarted.step3.description'),
          },
        ],
      }),
    },
  ],
})

// FAQ state
const activeFaq = ref<number | null>(null)

// FAQ data
const faqs = [
  {
    question: 'support.faq.items.installation.question',
    answer: 'support.faq.items.installation.answer',
  },
  {
    question: 'support.faq.items.usage.question',
    answer: 'support.faq.items.usage.answer',
  },
  {
    question: 'support.faq.items.premium.question',
    answer: 'support.faq.items.premium.answer',
  },
  {
    question: 'support.faq.items.languages.question',
    answer: 'support.faq.items.languages.answer',
  },
  {
    question: 'support.faq.items.privacy.question',
    answer: 'support.faq.items.privacy.answer',
  },
  {
    question: 'support.faq.items.troubleshooting.question',
    answer: 'support.faq.items.troubleshooting.answer',
  },
]

// Functions
function toggleFaq(index: number): void {
  activeFaq.value = activeFaq.value === index ? null : index
}

function scrollToFaq(): void {
  const element = document.getElementById('faq')
  element?.scrollIntoView({ behavior: 'smooth' })
}

function scrollToGettingStarted(): void {
  const element = document.getElementById('getting-started')
  element?.scrollIntoView({ behavior: 'smooth' })
}

/**
 * Opens email client for general support
 */
async function openGeneralSupportEmail(): Promise<void> {
  emailStatus.value = 'loading'
  emailError.value = null

  try {
    logger.info('Attempting to open general support email', { locale: locale.value }, 'support')

    await openEmailWithI18nTemplate('GENERAL_SUPPORT', locale.value as 'en' | 'fr' | 'es')

    emailStatus.value = 'success'
    logger.info('General support email opened successfully', {}, 'support')

    // Reset status after a delay
    setTimeout(() => {
      emailStatus.value = 'idle'
    }, 3000)
  } catch (error) {
    logger.warn(
      'Failed to open general support email with template, using fallback',
      {
        locale: locale.value,
        error: error instanceof Error ? error.message : String(error),
      },
      'support'
    )

    try {
      // Fallback: try to open email using window.location
      window.location.href = 'mailto:contact@noorways.fr?subject=n.writer%20Support'

      emailStatus.value = 'success'
      logger.info('General support email fallback executed successfully', {}, 'support')

      // Reset status after a delay
      setTimeout(() => {
        emailStatus.value = 'idle'
      }, 3000)
    } catch (fallbackError) {
      emailStatus.value = 'error'
      emailError.value =
        'Unable to open email client. Please contact us directly at contact@noorways.fr'

      logger.error(
        'Both general support email methods failed',
        fallbackError instanceof Error ? fallbackError : undefined,
        {
          originalError: error instanceof Error ? error.message : String(error),
          locale: locale.value,
        },
        'support'
      )

      // Reset status after a longer delay for errors
      setTimeout(() => {
        emailStatus.value = 'idle'
        emailError.value = null
      }, 8000)
    }
  }
}

/**
 * Opens email client for extension support
 */
async function openSupportEmail(): Promise<void> {
  emailStatus.value = 'loading'
  emailError.value = null

  try {
    logger.info('Attempting to open support email', { locale: locale.value }, 'support')

    await openEmailWithI18nTemplate('EXTENSION_SUPPORT', locale.value as 'en' | 'fr' | 'es')

    emailStatus.value = 'success'
    logger.info('Support email opened successfully', {}, 'support')

    // Reset status after a delay
    setTimeout(() => {
      emailStatus.value = 'idle'
    }, 3000)
  } catch (error) {
    logger.warn(
      'Failed to open email with template, using fallback',
      {
        locale: locale.value,
        error: error instanceof Error ? error.message : String(error),
      },
      'support'
    )

    try {
      // Fallback: try to open email using window.location
      window.location.href = 'mailto:contact@noorways.fr?subject=n.writer%20Extension%20Support'

      emailStatus.value = 'success'

      // Reset status after a delay
      setTimeout(() => {
        emailStatus.value = 'idle'
      }, 3000)
    } catch (fallbackError) {
      emailStatus.value = 'error'
      emailError.value =
        'Unable to open email client. Please contact us directly at contact@noorways.fr'

      logger.error(
        'Both email methods failed',
        fallbackError instanceof Error ? fallbackError : undefined,
        {
          originalError: error instanceof Error ? error.message : String(error),
          locale: locale.value,
        },
        'support'
      )

      // Reset status after a longer delay for errors
      setTimeout(() => {
        emailStatus.value = 'idle'
        emailError.value = null
      }, 8000)
    }
  }
}
</script>

<style scoped>
.support {
  min-height: 100vh;
  padding: 2rem 0;
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1rem;
}

.support-header {
  text-align: center;
  margin-bottom: 3rem;
}

.support-header h1 {
  font-size: 2.5rem;
  font-weight: 700;
  color: var(--nw-brand-slate, #1e293b);
  margin-bottom: 1rem;
}

.support-description {
  font-size: 1.125rem;
  color: #64748b;
  max-width: 600px;
  margin: 0 auto;
}

.support-content {
  display: flex;
  flex-direction: column;
  gap: 3rem;
}

.support-section {
  background: white;
  border-radius: 1rem;
  padding: 2rem;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
}

.support-section h2 {
  font-size: 1.875rem;
  font-weight: 600;
  color: var(--nw-brand-slate, #1e293b);
  margin-bottom: 1.5rem;
}

/* Help Grid */
.help-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1.5rem;
}

.help-card {
  background: #f8fafc;
  border-radius: 0.75rem;
  padding: 1.5rem;
  text-align: center;
  border: 1px solid #e2e8f0;
  transition: all 0.3s ease;
}

.help-card:hover {
  border-color: var(--nw-brand-primary, #1e40af);
  box-shadow: 0 10px 25px -5px var(--nw-primary-20, rgba(30, 64, 175, 0.2));
  transform: translateY(-4px);
}

.help-icon {
  font-size: 2rem;
  margin-bottom: 1rem;
}

.help-card h3 {
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--nw-brand-slate, #1e293b);
  margin-bottom: 0.5rem;
}

.help-card p {
  color: #64748b;
  margin-bottom: 1rem;
}

/* Steps */
.steps-container {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.step {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
}

.step-number {
  background: linear-gradient(135deg, var(--nw-brand-primary, #1e40af), var(--nw-brand-primary-dark, #1e3a8a));
  color: white;
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  flex-shrink: 0;
  box-shadow: 0 4px 12px var(--nw-primary-30, rgba(30, 64, 175, 0.3));
}

.step-content h3 {
  font-size: 1.125rem;
  font-weight: 600;
  color: var(--nw-brand-slate, #1e293b);
  margin-bottom: 0.5rem;
}

.step-content p {
  color: #64748b;
}

/* FAQ */
.faq-container {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.faq-item {
  border: 1px solid #e2e8f0;
  border-radius: 0.75rem;
  overflow: hidden;
  transition: border-color 0.3s ease;
}

.faq-item:hover {
  border-color: var(--nw-brand-primary-light, #3b82f6);
}

.faq-question {
  width: 100%;
  padding: 1rem;
  background: #f8fafc;
  border: none;
  text-align: left;
  font-weight: 500;
  color: var(--nw-brand-slate, #1e293b);
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: background-color 0.2s;
}

.faq-question:hover {
  background: #f1f5f9;
}

.faq-item.active .faq-question {
  background: var(--nw-primary-10, rgba(30, 64, 175, 0.1));
  color: var(--nw-brand-primary-dark, #1e3a8a);
}

.faq-icon {
  font-size: 1.25rem;
  font-weight: 300;
  color: var(--nw-brand-primary, #1e40af);
}

.faq-answer {
  padding: 1rem;
  background: white;
  border-top: 1px solid #e2e8f0;
}

.faq-answer p {
  color: #64748b;
  margin: 0;
}

.faq-link {
  color: var(--nw-brand-primary, #1e40af);
  text-decoration: none;
  font-weight: 500;
}

.faq-link:hover {
  text-decoration: underline;
  color: var(--nw-brand-primary-dark, #1e3a8a);
}

/* Contact Info */
.contact-info {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.contact-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #64748b;
}

.contact-item strong {
  color: var(--nw-brand-slate, #1e293b);
}

.contact-item a {
  color: var(--nw-brand-primary, #1e40af);
  text-decoration: none;
}

.contact-item a:hover {
  text-decoration: underline;
  color: var(--nw-brand-primary-dark, #1e3a8a);
}

/* Extension Info */
.extension-info p {
  color: #64748b;
  margin-bottom: 1.5rem;
}

.extension-links {
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
}

/* Buttons */
.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.75rem 1.5rem;
  border-radius: 0.5rem;
  font-weight: 500;
  text-decoration: none;
  border: none;
  cursor: pointer;
  transition: all 0.3s ease;
}

.btn-primary {
  background: linear-gradient(135deg, var(--nw-brand-primary, #1e40af), var(--nw-brand-primary-dark, #1e3a8a));
  color: white;
  box-shadow: 0 4px 12px var(--nw-primary-30, rgba(30, 64, 175, 0.3));
}

.btn-primary:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px var(--nw-primary-40, rgba(30, 64, 175, 0.4));
}

.btn-secondary {
  background: var(--nw-brand-slate, #1e293b);
  color: white;
}

.btn-secondary:hover {
  background: #334155;
  transform: translateY(-2px);
}

.btn-outline {
  background: transparent;
  color: var(--nw-brand-primary, #1e40af);
  border: 2px solid var(--nw-brand-primary, #1e40af);
}

.btn-outline:hover {
  background: var(--nw-brand-primary, #1e40af);
  color: white;
  transform: translateY(-2px);
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

.btn-loading {
  position: relative;
  padding-left: 3rem;
}

.btn-loading::before {
  content: '';
  position: absolute;
  left: 1rem;
  top: 50%;
  transform: translateY(-50%);
  width: 1rem;
  height: 1rem;
  border: 2px solid transparent;
  border-top: 2px solid currentColor;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to {
    transform: translateY(-50%) rotate(360deg);
  }
}

.email-error {
  margin-top: 1rem;
  padding: 0.75rem;
  background: #fee2e2;
  border: 1px solid #fecaca;
  border-radius: 0.5rem;
}

.error-message {
  color: #dc2626;
  font-size: 0.875rem;
  margin: 0;
}

.email-success {
  margin-top: 1rem;
  padding: 0.75rem;
  background: var(--nw-primary-10, rgba(30, 64, 175, 0.1));
  border: 1px solid var(--nw-brand-primary-light, #3b82f6);
  border-radius: 0.5rem;
}

.success-message {
  color: var(--nw-brand-primary-dark, #1e3a8a);
  font-size: 0.875rem;
  margin: 0;
}

.success-message a {
  color: var(--nw-brand-primary, #1e40af);
  text-decoration: underline;
}

.email-link {
  color: var(--nw-brand-primary, #1e40af);
  text-decoration: none;
  cursor: pointer;
  font-weight: 500;
}

.email-link:hover {
  text-decoration: underline;
  color: var(--nw-brand-primary-dark, #1e3a8a);
}

/* Override email-link styles in success message context */
.success-message .email-link {
  color: var(--nw-brand-primary-dark, #1e3a8a);
  text-decoration: underline;
}

/* Responsive */
@media (max-width: 768px) {
  .support-header h1 {
    font-size: 2rem;
  }

  .help-grid {
    grid-template-columns: 1fr;
  }

  .step {
    flex-direction: column;
    text-align: center;
  }
}
</style>

<!-- Dark theme overrides - unscoped to properly target html.dark -->
<style lang="scss">
html.dark {
  .support {
    background: linear-gradient(135deg, #0F172A 0%, #1E293B 100%);

    .support-header {
      h1 {
        color: #F1F5F9;
      }

      .support-description {
        color: #CBD5E1;
      }
    }

    .support-section {
      background: #1E293B;
      box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);

      h2 {
        color: #F1F5F9;
      }
    }

    // Help cards
    .help-card {
      background: #0F172A;
      border-color: #334155;

      &:hover {
        border-color: rgba(30, 64, 175, 0.5);
        box-shadow: 0 10px 25px -5px rgba(30, 64, 175, 0.2);
      }

      h3 {
        color: #F1F5F9;
      }

      p {
        color: #CBD5E1;
      }
    }

    // Steps
    .step-content {
      h3 {
        color: #F1F5F9;
      }

      p {
        color: #CBD5E1;
      }
    }

    // FAQ
    .faq-item {
      border-color: #334155;

      &:hover {
        border-color: #3B82F6;
      }
    }

    .faq-question {
      background: #0F172A;
      color: #F1F5F9;

      &:hover {
        background: #1E293B;
      }
    }

    .faq-item.active .faq-question {
      background: rgba(30, 64, 175, 0.15);
      color: #3B82F6;
    }

    .faq-answer {
      background: #1E293B;
      border-top-color: #334155;

      p {
        color: #CBD5E1;
      }
    }

    .faq-link {
      color: #3B82F6;

      &:hover {
        color: #1E40AF;
      }
    }

    // Contact info
    .contact-item {
      color: #CBD5E1;

      strong {
        color: #F1F5F9;
      }

      a {
        color: #3B82F6;

        &:hover {
          color: #1E40AF;
        }
      }
    }

    // Extension info
    .extension-info p {
      color: #CBD5E1;
    }

    // Buttons
    .btn-secondary {
      background: #334155;

      &:hover {
        background: #475569;
      }
    }

    .btn-outline {
      color: #3B82F6;
      border-color: #3B82F6;

      &:hover {
        background: #3B82F6;
        color: #0F172A;
      }
    }

    // Email status messages
    .email-error {
      background: rgba(220, 38, 38, 0.15);
      border-color: rgba(220, 38, 38, 0.3);

      .error-message {
        color: #FCA5A5;
      }
    }

    .email-success {
      background: rgba(30, 64, 175, 0.15);
      border-color: rgba(59, 130, 246, 0.3);

      .success-message {
        color: #3B82F6;

        a {
          color: #3B82F6;
        }
      }
    }

    .email-link {
      color: #3B82F6;

      &:hover {
        color: #1E40AF;
      }
    }
  }
}
</style>
